import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Layout } from "../components/Layout"
import { DataTable } from "@/components/DataTable/DataTable"
import { Button } from "@/components/Button"
import { PageHeader } from "@/components/PageHeader"
import { Skeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Dialog } from "@/components/Dialog/Dialog"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { Input } from "@/components/Form/Input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Form/Select"
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import {
  useTeamDetail,
  useUpdateTeamMember,
  useRemoveTeamMember,
  useUpdateTeamFeatureFlag,
  useResendInvite,
  useResendAllInvites,
  FeatureFlagInfo,
  TeamExchangeLink,
} from "@/hooks/useTeamDetail"
import { useToast } from "@/components/Toast"
import { useRecomputeStorage } from "@/hooks/useStorage"
import { dateInputToIso } from "@/lib/createTeamAccess"
import { compPlanToast } from "@/lib/compPlanResult"
import { teamCountryLabel, teamGameLabel } from "@/lib/teamCountry"
import { client, APIError } from "@/api"
import {
  UserGroupIcon,
  UsersIcon,
  VideoCameraIcon,
  FilmIcon,
  ServerIcon,
  CalendarIcon,
  ArrowLeftIcon,
  PencilIcon,
  UserPlusIcon,
  EnvelopeIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldExclamationIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline"
import {
  useTeamSubscriptionStatus,
  useAttachTeamSubscription,
  useCompTeamSubscription,
  useRevokeTeamComp,
  useCreateTeamTrial,
} from "@/hooks/useTeamSubscription"
import { useSubscriptionPlans, type SubscriptionPlan } from "@/hooks/useSubscriptionPlans"
import { ColumnDef } from "@tanstack/react-table"
import { formatDistanceToNow } from "date-fns"
import { useTeamLimits } from "@/hooks/useLimits"
import { UsageChart } from "@/components/Charts/UsageChart"
import { StorageProgressBar } from "@/components/Charts/StorageProgressBar"
import { PlanDetails } from "@/components/SubscriptionPlan/PlanDetails"
import { LimitAdjustmentForm } from "@/components/SubscriptionPlan/LimitAdjustmentForm"
import { SubscriptionOverrideForm } from "@/components/SubscriptionOverrides/SubscriptionOverrideForm"
import { normalizeOverridePlanTier } from "@/components/SubscriptionOverrides/override-plan-tier"
import { Alert } from "@/components/Alert/Alert"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { Switch } from "@/components/Form/Switch"
import { TeamMemberImportDialog } from "@/components/TeamMemberImportDialog"
import { parseDbTimestampUtc } from "@/lib/dbTimestamp"

interface TeamMember {
  public_id: string
  name: string
  email: string
  role: string
  email_verified: boolean
  is_superuser: boolean
  suspended_at: string | null
}

type InviteDeliveryStatus = "queued" | "sent" | "delivered" | "bounced" | "complained" | "failed"

interface TeamInvite {
  public_id: string
  email: string
  role: string
  status: string
  created_at: string
  expires_at: string
  inviterName: string
  // Slice 4: latest delivery state for this invite's most recent send.
  deliveryStatus?: InviteDeliveryStatus | null
  lastEventAt?: string | null
}

// Map the raw email_events delivery status to a badge. `delivered` is the only
// "good" terminal state; bounced/complained/failed mean the invite never landed
// (the actual answer to "why hasn't this player accepted?"); sent/queued are
// in-flight; null = no send recorded (e.g. invited before delivery tracking).
function inviteDeliveryBadge(status: TeamInvite["deliveryStatus"]): {
  label: string
  className: string
} {
  switch (status) {
    case "delivered":
      return { label: "Delivered", className: "bg-green-100 text-green-800" }
    case "bounced":
      return { label: "Bounced", className: "bg-red-100 text-red-800" }
    case "complained":
      return { label: "Complained", className: "bg-red-100 text-red-800" }
    case "failed":
      return { label: "Send failed", className: "bg-red-100 text-red-800" }
    case "sent":
    case "queued":
      return { label: "Sent", className: "bg-blue-100 text-blue-800" }
    default:
      return { label: "Not tracked", className: "bg-gray-100 text-gray-800" }
  }
}

function exchangeStatusClassName(status: TeamExchangeLink["status"]) {
  switch (status) {
    case "accepted":
      return "bg-green-100 text-green-800"
    case "revoked":
    case "declined":
      return "bg-red-100 text-red-800"
    case "pending":
    default:
      return "bg-yellow-100 text-yellow-800"
  }
}

function formatOptionalDate(value: string | null) {
  if (!value) {
    return null
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }

  return date.toLocaleDateString()
}

function csvCell(value: string | number | boolean | null | undefined) {
  if (value === null || value === undefined) {
    return ""
  }

  const text = String(value)
  const spreadsheetSafeText = /^[=+\-@]/.test(text) ? `'${text}` : text

  if (/[",\n\r]/.test(spreadsheetSafeText)) {
    return `"${spreadsheetSafeText.replace(/"/g, "\"\"")}"`
  }

  return spreadsheetSafeText
}

function downloadCsv(filename: string, headers: string[], rows: Array<Array<string | number | boolean | null | undefined>>) {
  const csv = [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\n")
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" })
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement("a")

  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(url)
}

function filenameSafe(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
}

export function TeamDetail() {
  const { teamId } = useParams<{ teamId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data, isLoading, error, refetch } = useTeamDetail(teamId!)
  const { data: limitsData, refetch: refetchLimits } = useTeamLimits(teamId!)
  const recomputeStorage = useRecomputeStorage()

  const handleRecomputeStorage = () => {
    if (
      !window.confirm(
        "Recompute storage counters now? Every team's and user's counter is reset to the transcoded size of the sources its games hold, the same as the daily job. All of them, not just this team, because an organization team's usage is the sum of its sibling teams' counters. No files are deleted."
      )
    )
      return
    // A full run, not scoped to this team: an organization-owned team's displayed usage is pooled
    // across its sibling teams, so recomputing only this one could leave the shown total wrong.
    // Cheap at our size (about two rows read per clip).
    recomputeStorage.mutate(
      {},
      {
        onSuccess: (stats) => {
          void refetchLimits()
          toast({
            title: "Storage recomputed",
            description:
              stats.owners_changed > 0
                ? `${stats.owners_changed} counter(s) changed; total ${(stats.bytes_before / 1024 ** 3).toFixed(2)} GB → ${(stats.bytes_after / 1024 ** 3).toFixed(2)} GB.`
                : "All counters already matched the recomputed values.",
          })
        },
        onError: (err) =>
          toast({
            title: "Couldn't recompute storage",
            description: err.message,
            variant: "destructive",
          }),
      }
    )
  }
  const updateMember = useUpdateTeamMember()
  const removeMember = useRemoveTeamMember()
  const updateFeatureFlag = useUpdateTeamFeatureFlag()
  const resendInvite = useResendInvite()
  const resendAllInvites = useResendAllInvites()

  const [showResendAllDialog, setShowResendAllDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false)
  const [showImportMembersDialog, setShowImportMembersDialog] = useState(false)
  const [showRemoveMemberDialog, setShowRemoveMemberDialog] = useState(false)
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)

  const [editForm, setEditForm] = useState({
    name: "",
  })

  const [addMemberForm, setAddMemberForm] = useState<{
    userEmail: string
    userName: string
    role: string
    mode: "invite" | "activate"
  }>({
    userEmail: "",
    userName: "",
    role: "coach",
    // "invite": email them a setup link (they pick their own password).
    // "activate": create + add them now so they can log in immediately.
    mode: "invite",
  })

  const memberColumns: ColumnDef<TeamMember>[] = [
    {
      accessorKey: "name",
      header: "Member",
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.name}</p>
          <p className="text-sm text-secondary">{row.original.email}</p>
        </div>
      ),
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => (
        <Select
          value={row.original.role}
          onValueChange={(value) => handleRoleChange(row.original, value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="coach">Coach</SelectItem>
            <SelectItem value="player">Player</SelectItem>
            <SelectItem value="parent">Parent</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          {row.original.email_verified ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircleIcon className="h-3 w-3 mr-1" />
              Verified
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <XCircleIcon className="h-3 w-3 mr-1" />
              Unverified
            </span>
          )}
          {row.original.is_superuser && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
              Admin
            </span>
          )}
          {row.original.suspended_at && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
              <ShieldExclamationIcon className="h-3 w-3 mr-1" />
              Suspended
            </span>
          )}
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2 flex-shrink-0">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/users/${row.original.public_id}`)}
          >
            View User
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              setSelectedMember(row.original)
              setShowRemoveMemberDialog(true)
            }}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ]

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-64" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const { team, members, stats, invites, featureFlags = [], exchanges = [] } = data
  const pendingInvites =
    invites?.filter((invite) => invite.status.toLowerCase() === "pending") ?? []

  const handleResendInvite = async (invitePublicId: string, email: string) => {
    try {
      const result = await resendInvite.mutateAsync({ teamId: teamId!, invitePublicId })
      toast({
        title: "Invitation resent",
        description: result.expires_at
          ? `Sent to ${email}. Link expires ${new Date(result.expires_at).toLocaleString()}.`
          : `Invitation sent to ${email}.`,
      })
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof APIError && (e.status === 404 || e.status === 409)
          ? "Invitation changed or is no longer pending. Check the invitation list."
          : e instanceof Error ? e.message : "Failed to resend invitation.",
        variant: "destructive",
      })
    }
  }

  const handleResendAll = async () => {
    try {
      const res = await resendAllInvites.mutateAsync({ teamId: teamId! })
      toast({
        title: "Invitations resent",
        description: `Sent ${res.sent} of ${res.total} pending invite${
          res.total === 1 ? "" : "s"
        }.`,
      })
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof APIError && e.status === 409
          ? "Invitations changed. Check the invitation list before resending."
          : e instanceof Error ? e.message : "Failed to resend invitations.",
        variant: "destructive",
      })
    }
  }

  const describeFlagSource = (flag: FeatureFlagInfo) => {
    const defaultText = `Defaults to ${flag.defaultValue ? "On" : "Off"}`
    if (flag.teamOverride !== null) {
      return `Team override ${flag.teamOverride ? "enabled" : "disabled"} (${defaultText})`
    }
    if (flag.source === "organization") {
      return `Managed by organization (${defaultText})`
    }
    if (flag.source === "user") {
      return `Personal override active (${defaultText})`
    }
    return defaultText
  }

  const handleEdit = async () => {
    try {
      const response = await (client.admin.teams[":publicId"] as any).$put({
        param: { publicId: teamId! },
        json: editForm,
      })

      if (!response.ok) {
        const error = (await response.json()) as any
        throw new APIError(
          response.status,
          error.code || "TEAM_UPDATE_FAILED",
          new Error(error.message || error.error || "Failed to update team")
        )
      }

      toast({
        title: "Team updated",
        description: "The team details have been updated successfully.",
      })
      setShowEditDialog(false)
      refetch()
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof APIError
            ? error.originalError.message
            : "Failed to update team. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleAddMember = async () => {
    try {
      const response = await (client.admin.teams[":publicId"].members as any).$post({
        param: { publicId: teamId! },
        json: {
          email: addMemberForm.userEmail,
          name: addMemberForm.userName || undefined,
          role: addMemberForm.role,
          mode: addMemberForm.mode,
        },
      })

      if (!response.ok) {
        const error = (await response.json()) as any
        throw new APIError(
          response.status,
          error.code || "ADD_MEMBER_FAILED",
          new Error(error.message || "Failed to add member")
        )
      }

      const result = (await response.json()) as {
        invited?: boolean
        emailSent?: boolean
        userCreated?: boolean
      }
      if (result.invited) {
        toast(
          result.emailSent
            ? {
                title: "Invitation sent",
                description: `${addMemberForm.userEmail} was emailed a link to join the team.`,
              }
            : {
                title: "Invitation created",
                description:
                  "The invite was created, but the email failed to send. You can resend it from the pending invites list.",
                variant: "destructive",
              }
        )
      } else {
        toast({
          title: "Member added",
          description: result.userCreated
            ? "A new user was created and added to the team."
            : "The existing user was added to the team.",
        })
      }
      setShowAddMemberDialog(false)
      setAddMemberForm({ userEmail: "", userName: "", role: "coach", mode: "invite" })
      refetch()
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof APIError
            ? error.originalError.message
            : "Failed to add member. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleRemoveMember = async () => {
    if (!selectedMember) return

    try {
      await removeMember.mutateAsync({
        teamId: teamId!,
        userId: selectedMember.public_id,
      })

      toast({
        title: "Member removed",
        description: "The member has been removed from the team.",
      })
      setShowRemoveMemberDialog(false)
      setSelectedMember(null)
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to remove member. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleRoleChange = async (member: TeamMember, newRole: string) => {
    try {
      await updateMember.mutateAsync({
        teamId: teamId!,
        userId: member.public_id,
        role: newRole,
      })

      toast({
        title: "Role updated",
        description: "The member's role has been updated successfully.",
      })
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to update role. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleFeatureFlagToggle = async (flagKey: string, enabled: boolean) => {
    try {
      await updateFeatureFlag.mutateAsync({
        teamId: teamId!,
        flagKey,
        enabled,
      })

      toast({
        title: `Feature flag ${enabled ? "enabled" : "disabled"}`,
        description: "Changes apply immediately for this team.",
      })
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to update the feature flag. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleCopyLink = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast({
        title: "Link copied",
        description: `${label} copied to clipboard.`,
      })
    } catch (_error) {
      toast({
        title: "Copy failed",
        description: "Unable to copy this link from the browser.",
        variant: "destructive",
      })
    }
  }

  const handleDownloadMembers = () => {
    const today = new Date().toISOString().split("T")[0]
    const teamSlug = filenameSafe(team.name) || team.public_id

    downloadCsv(
      `${teamSlug}-team-members-${today}.csv`,
      ["Name", "Email", "Role", "Email verified", "Admin user", "Suspended at", "User ID"],
      members.map((member) => [
        member.name,
        member.email,
        member.role,
        member.email_verified ? "Yes" : "No",
        member.is_superuser ? "Yes" : "No",
        member.suspended_at,
        member.public_id,
      ])
    )
  }

  const handleDownloadPendingInvites = () => {
    const today = new Date().toISOString().split("T")[0]
    const teamSlug = filenameSafe(team.name) || team.public_id

    downloadCsv(
      `${teamSlug}-pending-invites-${today}.csv`,
      ["Email", "Role", "Status", "Invited by", "Created at", "Expires at", "Invite ID"],
      pendingInvites.map((invite: TeamInvite) => [
        invite.email,
        invite.role,
        invite.status,
        invite.inviterName,
        invite.created_at,
        invite.expires_at,
        invite.public_id,
      ])
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title={
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate("/teams")}
                className="p-2 hover:bg-surface rounded-md transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <span>Team Details</span>
            </div>
          }
          actions={
            <div className="flex items-center space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditForm({ name: team.name })
                  setShowEditDialog(true)
                }}
              >
                <PencilIcon className="h-4 w-4" />
                Edit
              </Button>
              <Button onClick={() => setShowAddMemberDialog(true)}>
                <UserPlusIcon className="h-4 w-4" />
                Add Member
              </Button>
              <Button variant="secondary" onClick={() => setShowImportMembersDialog(true)}>
                <ArrowUpTrayIcon className="h-4 w-4" />
                Bulk Import
              </Button>
            </div>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Team Info Card */}
            <div className="bg-surface rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Team Information</h2>

              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                    <UserGroupIcon className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-medium">{team.name}</h3>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-secondary">Team ID</p>
                        <p className="font-mono text-sm">{team.public_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-secondary">Created</p>
                        <p className="text-sm">
                          {new Date(team.created_at).toLocaleDateString()}
                          <span className="text-secondary ml-1">
                            ({formatDistanceToNow(new Date(team.created_at), { addSuffix: true })})
                          </span>
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-secondary">Country</p>
                        <p className="text-sm">{teamCountryLabel(team.country)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-secondary">Sport</p>
                        <p className="text-sm">{teamGameLabel(team.game, team.country)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Members Card */}
            <div className="bg-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Team Members ({members.length})</h2>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDownloadMembers}
                  disabled={members.length === 0}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download CSV
                </Button>
              </div>

              <DataTable columns={memberColumns} data={members} searchKey="name" />
            </div>

            {/* Usage Dashboard */}
            {limitsData && (
              <div className="bg-surface rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Usage Dashboard</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRecomputeStorage}
                    disabled={recomputeStorage.isPending}
                  >
                    {recomputeStorage.isPending ? "Recomputing..." : "Recompute storage"}
                  </Button>
                </div>

                {/* Storage Progress Bar */}
                <div className="mb-6">
                  <StorageProgressBar
                    usedGB={limitsData.usage.storage.used_gb}
                    limitGB={limitsData.usage.storage.limit_gb}
                    percentage={limitsData.usage.storage.percentage}
                  />
                </div>

                {/* Usage Chart */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-secondary">Resource Usage Overview</h3>
                  <UsageChart
                    data={[
                      {
                        name: "Games",
                        value: limitsData.usage.games.count,
                        limit: limitsData.usage.games.limit,
                        percentage: limitsData.usage.games.percentage,
                      },
                      ...(limitsData.usage.members
                        ? [
                            {
                              name: "Members",
                              value: limitsData.usage.members.count,
                              limit: limitsData.usage.members.limit,
                              percentage: limitsData.usage.members.percentage,
                            },
                          ]
                        : []),
                    ]}
                  />
                </div>

                {/* Usage Alerts */}
                {(limitsData.usage.storage.percentage >= 90 ||
                  limitsData.usage.games.percentage >= 90 ||
                  (limitsData.usage.members && limitsData.usage.members.percentage >= 90)) && (
                  <Alert variant="destructive" className="mt-4">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <div>
                      <p className="font-medium">High Usage Alert</p>
                      <p className="text-sm mt-1">
                        One or more resources are approaching their limits. Consider upgrading the
                        plan.
                      </p>
                    </div>
                  </Alert>
                )}
              </div>
            )}

            {/* Invitations Card */}
            <div className="bg-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                  Pending Invitations ({pendingInvites.length})
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowResendAllDialog(true)}
                    disabled={
                      pendingInvites.length === 0 ||
                      resendAllInvites.isPending ||
                      resendInvite.isPending
                    }
                  >
                    <EnvelopeIcon className="h-4 w-4" />
                    {resendAllInvites.isPending ? "Resending…" : "Resend all"}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleDownloadPendingInvites}
                    disabled={pendingInvites.length === 0}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Download CSV
                  </Button>
                </div>
              </div>

              {pendingInvites.length === 0 ? (
                <p className="text-sm text-secondary">No pending invitations for this team.</p>
              ) : (
                <div className="space-y-3">
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite.public_id}
                      className="flex items-center justify-between p-3 bg-background rounded-md"
                    >
                      <div className="flex items-center space-x-3">
                        <EnvelopeIcon className="h-5 w-5 text-secondary" />
                        <div>
                          <p className="font-medium">{invite.email}</p>
                          <p className="text-sm text-secondary">
                            Role: {invite.role} • Invited by {invite.inviterName} • Expires{" "}
                            {formatDistanceToNow(new Date(invite.expires_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(() => {
                          const badge = inviteDeliveryBadge(invite.deliveryStatus)
                          return (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                              title={
                                invite.lastEventAt
                                  ? `Last email event ${formatDistanceToNow(
                                      parseDbTimestampUtc(invite.lastEventAt),
                                      { addSuffix: true }
                                    )}`
                                  : "No delivery event recorded for this invite"
                              }
                            >
                              {badge.label}
                            </span>
                          )
                        })()}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleResendInvite(invite.public_id, invite.email)}
                          disabled={
                            resendAllInvites.isPending ||
                            (resendInvite.isPending &&
                              resendInvite.variables?.invitePublicId === invite.public_id)
                          }
                        >
                          {resendInvite.isPending &&
                          resendInvite.variables?.invitePublicId === invite.public_id
                            ? "Resending…"
                            : "Resend"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Film Share Links */}
            <div className="bg-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Film Share Links ({exchanges.length})</h2>
              </div>

              {exchanges.length === 0 ? (
                <p className="text-sm text-secondary">No outbound film shares from this team.</p>
              ) : (
                <div className="space-y-3">
                  {exchanges.map((exchange) => {
                    const gameDate = formatOptionalDate(exchange.source_game_date)
                    const title = exchange.source_game_title || "Untitled game"

                    return (
                      <div key={exchange.public_id} className="rounded-md bg-background p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium">{title}</p>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${exchangeStatusClassName(
                                  exchange.status
                                )}`}
                              >
                                {exchange.status}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-secondary">
                              To {exchange.recipient_email} • Sent by {exchange.sender_name}
                              {gameDate ? ` • ${gameDate}` : ""}
                            </p>
                            <p className="mt-1 text-xs text-secondary">
                              Created{" "}
                              {formatDistanceToNow(new Date(exchange.created_at), {
                                addSuffix: true,
                              })}
                              {exchange.includes_game_data ? " • game data included" : ""}
                              {exchange.include_annotations ? " • annotations included" : ""}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 space-y-3">
                          <div>
                            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-secondary">
                              Exchange link
                            </p>
                            <div className="flex flex-col gap-2 sm:flex-row">
                              <a
                                href={exchange.accept_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-secondary px-3 text-xs font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
                              >
                                <LinkIcon className="h-4 w-4" />
                                Open
                              </a>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  void handleCopyLink(exchange.accept_url, "Exchange link")
                                }
                              >
                                <ClipboardDocumentIcon className="h-4 w-4" />
                                Copy
                              </Button>
                              <p className="min-w-0 flex-1 break-all rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-secondary">
                                {exchange.accept_url}
                              </p>
                            </div>
                          </div>

                          {exchange.can_download && (
                            <div>
                              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-secondary">
                                ZIP download
                              </p>
                              <div className="flex flex-col gap-2 sm:flex-row">
                                <a
                                  href={exchange.download_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-secondary px-3 text-xs font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
                                >
                                  <ArrowDownTrayIcon className="h-4 w-4" />
                                  Open
                                </a>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    void handleCopyLink(exchange.download_url, "ZIP download link")
                                  }
                                >
                                  <ClipboardDocumentIcon className="h-4 w-4" />
                                  Copy
                                </Button>
                                <p className="min-w-0 flex-1 break-all rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-secondary">
                                  {exchange.download_url}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Stats Card with Limits */}
            <div className="bg-surface rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Team Stats</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <UsersIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Members</span>
                  </div>
                  <span className="font-medium">
                    {stats.memberCount}
                    {/* 9999 is the plans table's "unlimited": no cap worth showing */}
                    {limitsData &&
                      limitsData.limits.max_team_members &&
                      limitsData.limits.max_team_members < 9999 && (
                      <span className="text-secondary text-sm">
                        {" "}
                        / {limitsData.limits.max_team_members}
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <VideoCameraIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Games</span>
                  </div>
                  <span className="font-medium">
                    {stats.gameCount}
                    {limitsData && (
                      <span className="text-secondary text-sm">
                        {" "}
                        / {limitsData.limits.max_games === -1 ? "∞" : limitsData.limits.max_games}
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FilmIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Clips</span>
                  </div>
                  <span className="font-medium">
                    {stats.clipCount}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ServerIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Storage</span>
                  </div>
                  <span className="font-medium">
                    {stats.storageUsedGB} GB
                    {limitsData && (
                      <span className="text-secondary text-sm">
                        {" "}
                        / {limitsData.limits.storage_gb} GB
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Subscription Admin Panel */}
            <SubscriptionAdminPanel teamId={teamId!} onLimitsChanged={() => void refetchLimits()} />

            {/* Subscription Plan */}
            {limitsData && <PlanDetails plan={limitsData.plan} limits={limitsData.limits} />}

            {/* Limit Adjustment Form (Enterprise Only) */}
            {limitsData && (
              <LimitAdjustmentForm
                teamId={teamId!}
                currentLimits={limitsData.limits}
                isEnterprise={limitsData.plan.tier === "enterprise"}
                onUpdate={() => refetch()}
              />
            )}

            {/* Feature Flags */}
            <div className="bg-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Feature Flags</h2>
                {updateFeatureFlag.isPending && (
                  <span className="text-xs text-secondary">Saving…</span>
                )}
              </div>
              {featureFlags.length === 0 ? (
                <p className="text-sm text-secondary">No feature flags are defined yet.</p>
              ) : (
                <div className="space-y-5">
                  {featureFlags.map((flag) => (
                    <div key={flag.key} className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium">{flag.name}</p>
                        {flag.description && (
                          <p className="text-sm text-secondary mt-1">{flag.description}</p>
                        )}
                        <p className="text-xs text-secondary mt-1">{describeFlagSource(flag)}</p>
                      </div>
                      <Switch
                        checked={flag.teamOverride ?? flag.enabled}
                        onCheckedChange={(checked) => handleFeatureFlagToggle(flag.key, checked)}
                        disabled={updateFeatureFlag.isPending}
                        aria-label={`Toggle ${flag.name}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions Card */}
            <div className="bg-surface rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Team Actions</h2>

              <div className="space-y-3">
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => navigate(`/teams/${teamId}/activity`)}
                >
                  <CalendarIcon className="h-4 w-4" />
                  View Activity Log
                </Button>
                <Button variant="destructive" className="w-full" disabled>
                  <TrashIcon className="h-4 w-4" />
                  Delete Team
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
            <DialogDescription>Update the team's information.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Team Name</label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ name: e.target.value })}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleEdit}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {addMemberForm.mode === "invite" ? "Invite Team Member" : "Add Team Member"}
            </DialogTitle>
            <DialogDescription>
              {addMemberForm.mode === "invite"
                ? "Email this person a link to join the team — they'll set up their own account."
                : "Create the account and add them to the team now so they can log in right away."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">How to add</label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={addMemberForm.mode === "invite" ? "default" : "secondary"}
                  onClick={() => setAddMemberForm({ ...addMemberForm, mode: "invite" })}
                >
                  Invite
                </Button>
                <Button
                  type="button"
                  variant={addMemberForm.mode === "activate" ? "default" : "secondary"}
                  onClick={() => setAddMemberForm({ ...addMemberForm, mode: "activate" })}
                >
                  Activate now
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                value={addMemberForm.userEmail}
                onChange={(e) => setAddMemberForm({ ...addMemberForm, userEmail: e.target.value })}
                placeholder="user@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {addMemberForm.mode === "invite"
                  ? "Name (required)"
                  : "Name (required for new users)"}
              </label>
              <Input
                value={addMemberForm.userName}
                onChange={(e) => setAddMemberForm({ ...addMemberForm, userName: e.target.value })}
                placeholder="John Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Role</label>
              <Select
                value={addMemberForm.role}
                onValueChange={(value) => setAddMemberForm({ ...addMemberForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="player">Player</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setShowAddMemberDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddMember}
                disabled={
                  !addMemberForm.userEmail ||
                  (addMemberForm.mode === "invite" && !addMemberForm.userName)
                }
              >
                {addMemberForm.mode === "invite" ? "Send Invite" : "Add Member"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TeamMemberImportDialog
        open={showImportMembersDialog}
        onOpenChange={setShowImportMembersDialog}
        teamId={teamId!}
        onComplete={() => {
          refetch()
          refetchLimits()
        }}
      />

      {/* Remove Member Confirmation */}
      <ConfirmDialog
        open={showRemoveMemberDialog}
        onOpenChange={setShowRemoveMemberDialog}
        title="Remove Team Member"
        description={`Are you sure you want to remove ${selectedMember?.name} from this team?`}
        onConfirm={handleRemoveMember}
        confirmVariant="destructive"
      />
      <ConfirmDialog
        open={showResendAllDialog}
        onOpenChange={setShowResendAllDialog}
        title="Resend all pending invitations"
        description={`Send a fresh invite email to all ${pendingInvites.length} pending invitee${
          pendingInvites.length === 1 ? "" : "s"
        }? Unexpired links keep their original expiry. Expired or missing links are renewed for 7 days.`}
        onConfirm={handleResendAll}
      />
    </Layout>
  )
}

/**
 * Admin panel for viewing/attaching a team's subscription plan.
 * Uses admin endpoints:
 *  - GET /admin/subscriptions/:teamPublicId/status
 *  - POST /admin/subscriptions/attach
 */
function SubscriptionAdminPanel({
  teamId,
  onLimitsChanged,
}: {
  teamId: string
  onLimitsChanged?: () => void
}) {
  const { toast } = useToast()
  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
  } = useTeamSubscriptionStatus(teamId)
  const { data: plansData, isLoading: plansLoading, error: plansError } = useSubscriptionPlans()
  const attachMutation = useAttachTeamSubscription()
  const createTrialMutation = useCreateTeamTrial()
  const compMutation = useCompTeamSubscription()
  const revokeCompMutation = useRevokeTeamComp()

  const [selectedPlanId, setSelectedPlanId] = useState<string>("")
  const [expiresAt, setExpiresAt] = useState<string>("")
  // The escape hatch for the attach guard. Off by default and reset after every
  // attach: it is meant to be a decision taken once, for one grant, not a mode
  // the panel quietly stays in.
  const [allowUnbackedGrant, setAllowUnbackedGrant] = useState(false)
  const [compPlanId, setCompPlanId] = useState<string>("")
  const [compExpiresAt, setCompExpiresAt] = useState<string>("")
  const [compReason, setCompReason] = useState<string>("")
  const [showRevokeCompDialog, setShowRevokeCompDialog] = useState(false)
  const [showCompDialog, setShowCompDialog] = useState(false)
  const [showForcedAttachDialog, setShowForcedAttachDialog] = useState(false)

  const subscription = statusData?.subscription ?? null
  const subscriptionPlans: SubscriptionPlan[] = plansData?.plans ?? []
  const overridePlanTier = normalizeOverridePlanTier(subscription?.plan.tier)

  const onCreateTrial = async () => {
    try {
      await createTrialMutation.mutateAsync({ teamPublicId: teamId })
      toast({
        title: "Trial created",
        description: "14-day trial subscription has been created for this team.",
      })
    } catch (e) {
      toast({
        title: "Create trial failed",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  // Validate BEFORE opening the dialog, so a missing plan or reason is an inline
  // correction rather than a confirmation prompt that then fails.
  const onCompRequested = () => {
    if (!compPlanId) {
      toast({
        title: "Select a plan",
        description: "Choose the plan to comp.",
        variant: "destructive",
      })
      return
    }
    if (!compReason.trim()) {
      toast({
        title: "Reason required",
        description: "The reason is what marks this subscription as a comp in Stripe and the DB.",
        variant: "destructive",
      })
      return
    }
    setShowCompDialog(true)
  }

  const onComp = async () => {
    if (!compPlanId) {
      toast({
        title: "Select a plan",
        description: "Choose the plan to comp.",
        variant: "destructive",
      })
      return
    }
    if (!compReason.trim()) {
      toast({
        title: "Reason required",
        description: "The reason is what marks this subscription as a comp in Stripe and the DB.",
        variant: "destructive",
      })
      return
    }
    try {
      const result = await compMutation.mutateAsync({
        teamPublicId: teamId,
        planId: compPlanId,
        reason: compReason.trim(),
        // `null`, not `undefined`. JSON drops an undefined property, and the
        // endpoint reads the key's PRESENCE as "the caller stated an expiry" —
        // an omitted date means "finish the job", a null one means "no expiry".
        // Sending undefined for a cleared field would make a request to drop an
        // existing comp's end date look like a retry, and the admin would be told
        // it worked while Stripe kept the old `cancel_at`.
        expiresAt: dateInputToIso(compExpiresAt),
      })
      toast(compPlanToast(result))
      setCompPlanId("")
      setCompExpiresAt("")
      setCompReason("")
    } catch (e) {
      toast({
        title: "Comp failed",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const onRevokeComp = async () => {
    try {
      const result = await revokeCompMutation.mutateAsync({ teamPublicId: teamId })
      toast({
        title: "Comp revoked",
        description:
          result.accessResolution === "revoked"
            ? "Cancelled in Stripe. The team no longer has access."
            : "Cancelled in Stripe. The team keeps access from another subscription.",
      })
    } catch (e) {
      toast({
        title: "Revoke failed",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const onAttach = async () => {
    if (!selectedPlanId) {
      toast({
        title: "Select a plan",
        description: "Choose a plan to attach.",
        variant: "destructive",
      })
      return
    }
    try {
      const result = await attachMutation.mutateAsync({
        teamPublicId: teamId,
        planId: selectedPlanId,
        status: "active",
        expiresAt: dateInputToIso(expiresAt) ?? undefined,
        // Omitted unless ticked, so the default request is the one the server
        // refuses on a priced plan.
        allowUnbackedGrant: allowUnbackedGrant || undefined,
      })
      // Don't report a durable success the server already knows is temporary.
      if (result.overriddenByBillingRow) {
        toast({
          title: "Attached, but it won't stick",
          description: result.billingRowGrantsAccess
            ? `This team has a live "${result.billingRowPlanId}" subscription. The next Stripe event for it will restore that plan. Use Comp Plan to make the grant durable.`
            : "This team has a cancelled subscription on file. The next Stripe event for it will revoke this plan entirely. Use Comp Plan to make the grant durable.",
          variant: "destructive",
        })
      } else {
        toast({ title: "Subscription updated", description: "Plan attached to team." })
      }
      setSelectedPlanId("")
      setExpiresAt("")
    } catch (e) {
      toast({
        title: "Attach failed",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    } finally {
      // Cleared on failure too, deliberately. The plan and date survive a failed
      // attach so a retry is one click — but if the override survived with them,
      // one forced grant would silently become a mode, and the next retry (or the
      // next plan the admin picks) would be forced without anyone choosing it.
      // Re-ticking a box is the correct cost of that decision.
      setAllowUnbackedGrant(false)
    }
  }

  // A plain attach needs no confirmation — the server refuses the dangerous shape
  // on its own. A FORCED one has just told the server to stand down, so this
  // dialog is the last thing between a stray click and access with no billing
  // behind it. Same treatment Comp and Revoke already get.
  const onAttachRequested = () => {
    if (!selectedPlanId) {
      toast({
        title: "Select a plan",
        description: "Choose a plan to attach.",
        variant: "destructive",
      })
      return
    }
    if (allowUnbackedGrant) {
      setShowForcedAttachDialog(true)
      return
    }
    void onAttach()
  }

  return (
    <div className="bg-surface rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Subscription</h2>
      </div>

      {statusLoading ? (
        <p className="text-sm text-secondary">Loading status...</p>
      ) : statusError ? (
        <p className="text-sm text-destructive">Failed to load subscription status</p>
      ) : (
        <div className="space-y-2">
          <p className="text-sm">
            Current:{" "}
            {subscription ? (
              <span className="font-medium">
                {subscription.plan.name} ({subscription.plan.tier}) —{" "}
                {/* The row still SAYS `active`; since #1463 that alone no longer
                    grants. Lead with the effective answer so this line cannot
                    read as working access, and keep the stored value visible
                    beside it so the page and a hand-run SELECT agree. */}
                {subscription.expired ? (
                  <span className="text-red-600">
                    expired {formatOptionalDate(subscription.expires_at)} (row says{" "}
                    {subscription.status})
                  </span>
                ) : (
                  <>
                    {subscription.status}
                    {subscription.expires_at
                      ? ` — expires ${formatOptionalDate(subscription.expires_at)}`
                      : ""}
                  </>
                )}
              </span>
            ) : (
              <span className="text-secondary">No active subscription</span>
            )}
          </p>
          {subscription?.expired && (
            <p className="text-xs text-secondary">
              The grant's end date has passed, so the team is on the free tier — limits and features
              resolve as if it had no plan. The row is still here: comp or re-attach it with a future
              date to restore access, or leave it as the record of a grant that ended.
            </p>
          )}
        </div>
      )}

      {!subscription && !statusLoading && (
        <div className="mt-4">
          <Button onClick={onCreateTrial} disabled={createTrialMutation.isPending}>
            {createTrialMutation.isPending ? "Creating Trial..." : "Create 14-Day Trial"}
          </Button>
        </div>
      )}

      {/* The preferred way to give a team a plan for free. Creates a real Stripe
          subscription at 100% off, so the grant has a billing row, shows up on the
          Subscriptions page, and survives webhooks for the team's other
          subscriptions. See issue #1387. */}
      <div className="mt-6 space-y-2 border-t border-border pt-4">
        <label className="block text-sm font-medium">Comp Plan</label>
        <p className="text-xs text-secondary">
          Creates the subscription in Stripe at 100% off. Leave the date empty for a
          perpetual comp; otherwise Stripe cancels it on that date.
        </p>
        {plansLoading ? (
          <p className="text-sm text-secondary">Loading plans...</p>
        ) : plansError ? (
          <p className="text-sm text-destructive">Failed to load plans</p>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="border border-border rounded-md bg-background px-2 py-2 text-sm"
                value={compPlanId}
                onChange={(e) => setCompPlanId(e.target.value)}
              >
                <option value="">Select a plan</option>
                {subscriptionPlans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.tier})
                  </option>
                ))}
              </select>
              <input
                type="date"
                className="border border-border rounded-md bg-background px-2 py-2 text-sm"
                value={compExpiresAt}
                onChange={(e) => setCompExpiresAt(e.target.value)}
              />
            </div>
            <input
              type="text"
              className="w-full border border-border rounded-md bg-background px-2 py-2 text-sm"
              placeholder="Reason (required) — e.g. league partnership through 2027"
              value={compReason}
              onChange={(e) => setCompReason(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-2">
              {/* Confirmed: this creates a real, persistent Stripe subscription
                  and changes the team's access. Undoing a stray click means
                  cancelling an external subscription. */}
              <Button onClick={onCompRequested} disabled={compMutation.isPending}>
                {compMutation.isPending ? "Comping..." : "Comp Plan"}
              </Button>
              {/* Confirmed, not immediate: this cancels a live Stripe
                  subscription and can take the team's access away — an outward-
                  facing change a stray click shouldn't be able to make. */}
              <Button
                variant="outline"
                onClick={() => setShowRevokeCompDialog(true)}
                disabled={revokeCompMutation.isPending}
              >
                {revokeCompMutation.isPending ? "Revoking..." : "Revoke Comp"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 space-y-2 border-t border-border pt-4">
        <label className="block text-sm font-medium">Attach/Replace Plan</label>
        <p className="text-xs text-secondary">
          Database-only. Stripe is never told, so this grant is invisible on the
          Subscriptions page and a webhook for another of the team&apos;s subscriptions can
          overwrite it. Use <span className="font-medium">Comp Plan</span> above unless the
          plan has no Stripe price.
        </p>
        {plansLoading ? (
          <p className="text-sm text-secondary">Loading plans...</p>
        ) : plansError ? (
          <p className="text-sm text-destructive">Failed to load plans</p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="border border-border rounded-md bg-background px-2 py-2 text-sm"
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
            >
              <option value="">Select a plan</option>
              {subscriptionPlans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.tier})
                </option>
              ))}
            </select>
            <input
              type="date"
              className="border border-border rounded-md bg-background px-2 py-2 text-sm"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            <Button
              variant="outline"
              onClick={onAttachRequested}
              disabled={attachMutation.isPending}
            >
              {attachMutation.isPending ? "Attaching..." : "Attach Plan"}
            </Button>
            {/* The server refuses an active attach on a plan that has a Stripe
                price and tells you to comp it. This is the way past that, and it
                is here rather than curl-only so an outage doesn't leave the
                on-call admin hand-signing an API request. */}
            <label className="flex w-full items-start gap-2 text-xs text-secondary">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={allowUnbackedGrant}
                onChange={(e) => setAllowUnbackedGrant(e.target.checked)}
              />
              <span>
                Force an unbacked grant on a priced plan. Only when Stripe itself is
                unusable — the grant has no billing row, so a webhook for another of the
                team&apos;s subscriptions can revoke it.
              </span>
            </label>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showCompDialog}
        onOpenChange={setShowCompDialog}
        title="Comp this team?"
        description={`Creates a real Stripe subscription for "${compPlanId}" at 100% off${
          compExpiresAt ? `, ending ${compExpiresAt}` : ", with no end date"
        }. The team's access is then derived from it.`}
        confirmLabel="Create comp"
        onConfirm={onComp}
      />

      <ConfirmDialog
        open={showForcedAttachDialog}
        onOpenChange={setShowForcedAttachDialog}
        title="Force an unbacked grant?"
        description={`Gives this team "${selectedPlanId}" with no subscription behind it. It will not appear on the Subscriptions page, and a webhook for any other subscription this team has can revoke it. Only do this if Stripe is unusable — otherwise use Comp Plan.`}
        confirmLabel="Force the grant"
        confirmVariant="destructive"
        onConfirm={() => void onAttach()}
        onCancel={() => setAllowUnbackedGrant(false)}
      />

      <ConfirmDialog
        open={showRevokeCompDialog}
        onOpenChange={setShowRevokeCompDialog}
        title="Revoke this team's comp?"
        description="Cancels the comped subscription in Stripe. The team loses access unless another live subscription still grants it. This cannot be undone — re-comping creates a new subscription."
        confirmLabel="Revoke comp"
        confirmVariant="destructive"
        onConfirm={onRevokeComp}
      />

      {subscription && overridePlanTier && (
        <div className="mt-6">
          <SubscriptionOverrideForm
            teamPublicId={teamId}
            trialEndDate={null}
            initialPlanTier={overridePlanTier}
            allowTierSelection={false}
            embedded
            onSaved={onLimitsChanged}
          />
        </div>
      )}
    </div>
  )
}
